/** Longest edge (px) we keep for yard/BOL photo uploads. */
export const MAX_IMAGE_EDGE = 1600;
/** JPEG quality used when re-encoding a downscaled capture. */
export const IMAGE_QUALITY = 0.8;

/**
 * Downscale + re-encode a camera capture before upload.
 *
 * Always fails open: if the browser can't decode the file (HEIC on Chrome,
 * for example), canvas/`createImageBitmap` is unavailable, or the re-encoded
 * result isn't actually smaller, the original File is returned untouched so
 * the upload path behaves exactly as before.
 */
export async function compressImageFile(
  file: File,
  opts: { maxEdge?: number; quality?: number } = {},
): Promise<File> {
  const maxEdge = opts.maxEdge ?? MAX_IMAGE_EDGE;
  const quality = opts.quality ?? IMAGE_QUALITY;

  if (!file.type.startsWith("image/")) return file;
  if (typeof document === "undefined" || typeof createImageBitmap !== "function") return file;

  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    if (!width || !height) {
      bitmap.close?.();
      return file;
    }

    const scale = Math.min(1, maxEdge / Math.max(width, height));
    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close?.();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality),
    );
    if (!blob || blob.size >= file.size) return file;

    const base = file.name.replace(/\.[^.]+$/, "") || "photo";
    return new File([blob], `${base}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch (err) {
    console.warn("Image compression skipped:", err);
    return file;
  }
}
